import { useState } from 'react'
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material'
import { DeleteForever, Download, ManageSearch, PersonSearch } from '@mui/icons-material'

import {
    useDownloadSubjectExportMutation,
    useListPrivacyRequestsQuery,
    useRequestSubjectAccessMutation,
    useRequestSubjectErasureMutation,
} from '@/apis/opsApi'
import type { PrivacySubjectRequest } from '@/apis/opsTypes'
import { v2ErrorMessage } from '@/apis/v2Mappers'
import { useAppDispatch } from '@/app/hooks'
import { showNotification } from '@/app/notificationSlice'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'
import { useProjectId } from '@/hooks/useReportArgs'
import { ReportHeader, ReportStatus, fmtDateTime } from '@/modules/reports/common'
import { downloadText } from '@/utils/download'

import {
    SUBJECT_INPUTS,
    describeCounts,
    isOpen,
    toSubject,
    type SubjectInputKind,
} from './privacyModel'

const STATUS_COLOR: Record<
    PrivacySubjectRequest['status'],
    'default' | 'info' | 'success' | 'error'
> = {
    queued: 'default',
    running: 'info',
    completed: 'success',
    failed: 'error',
}

/**
 * Data-subject requests (DPA §9): find, export or erase everything this project holds about
 * one end user, by visitor id, user id or email. The work runs in the background (minutes);
 * the list refreshes while a request is open.
 */
export function PrivacyRequestsPage() {
    const projectId = useProjectId()
    const dispatch = useAppDispatch()
    const [kind, setKind] = useState<SubjectInputKind>('visitor')
    const [value, setValue] = useState('')
    const [confirmErase, setConfirmErase] = useState(false)
    const { data, isLoading, error } = useListPrivacyRequestsQuery(projectId, {
        pollingInterval: 15_000,
        skipPollingIfUnfocused: true,
    })
    const [access, accessState] = useRequestSubjectAccessMutation()
    const [erase, eraseState] = useRequestSubjectErasureMutation()
    const [download] = useDownloadSubjectExportMutation()

    const notify = (message: string, severity: 'success' | 'error' | 'info' = 'success') =>
        dispatch(showNotification({ message, severity }))

    const submit = async (what: 'access' | 'erasure') => {
        try {
            const s = await toSubject(kind, value)
            const r =
                what === 'access'
                    ? await access({ projectId, ...s }).unwrap()
                    : await erase({ projectId, ...s }).unwrap()
            notify(
                what === 'access'
                    ? r.export_available
                        ? 'The export is ready to download.'
                        : 'Collecting the data. The export appears below when it is ready.'
                    : 'Erasure requested. Reports stop showing this person now; stored data is removed in the background.',
            )
            setValue('')
        } catch (err) {
            notify(v2ErrorMessage(err) ?? 'Request failed', 'error')
        }
    }

    const save = async (r: PrivacySubjectRequest) => {
        try {
            const text = await download({ projectId, id: r.id }).unwrap()
            downloadText(`subject-${r.id}.ndjson`, text, 'application/x-ndjson')
        } catch (err) {
            notify(v2ErrorMessage(err) ?? 'Download failed', 'error')
        }
    }

    const help = SUBJECT_INPUTS.find(i => i.value === kind)?.help
    const busy = accessState.isLoading || eraseState.isLoading
    const open = (data ?? []).filter(isOpen).length

    return (
        <Box sx={{ p: 3 }}>
            <ReportHeader
                title="Privacy requests"
                subtitle="Answer access and erasure requests from your visitors and users yourself."
            />
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box
                        sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}
                    >
                        <TextField
                            select
                            size="small"
                            label="Find by"
                            value={kind}
                            onChange={e => setKind(e.target.value as SubjectInputKind)}
                            sx={{ minWidth: 200 }}
                        >
                            {SUBJECT_INPUTS.map(i => (
                                <MenuItem key={i.value} value={i.value}>
                                    {i.label}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            size="small"
                            label={SUBJECT_INPUTS.find(i => i.value === kind)?.label}
                            value={value}
                            onChange={e => setValue(e.target.value)}
                            helperText={help}
                            sx={{ flex: 1, minWidth: 260 }}
                        />
                        <Button
                            variant="contained"
                            startIcon={<PersonSearch />}
                            disabled={busy || value.trim() === ''}
                            onClick={() => {
                                void submit('access')
                            }}
                        >
                            Find and export
                        </Button>
                        <Button
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteForever />}
                            disabled={busy || value.trim() === ''}
                            onClick={() => setConfirmErase(true)}
                        >
                            Erase
                        </Button>
                    </Box>
                    <Alert severity="info" sx={{ mt: 2 }}>
                        A request covers the raw archive, exact events, queued events and the
                        delivery log of this project. The sampled report store keeps visitor ids for
                        90 days and cannot delete single rows: after an erasure those ids are
                        excluded from every report and export until they expire. Data already sent
                        to your destinations or warehouse is in your systems: handle it there too.
                    </Alert>
                </CardContent>
            </Card>

            <ReportStatus
                isLoading={isLoading}
                error={error}
                isEmpty={!!data && data.length === 0}
                emptyIcon={<ManageSearch />}
                emptyTitle="No requests yet"
                emptyDescription="Requests and their results are listed here; exports stay downloadable for 7 days."
            >
                <Card>
                    <CardContent>
                        {open > 0 && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {open} request{open === 1 ? '' : 's'} in progress; this list
                                refreshes on its own.
                            </Typography>
                        )}
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Requested</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Subject</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Result</TableCell>
                                    <TableCell align="right" />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(data ?? []).map(r => (
                                    <TableRow key={r.id}>
                                        <TableCell>{fmtDateTime(r.created_at)}</TableCell>
                                        <TableCell>
                                            {r.kind === 'access' ? 'Access' : 'Erasure'}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {r.subject_type.replace('_', ' ')}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ fontFamily: 'monospace' }}
                                            >
                                                sha256 {r.subject_sha256.slice(0, 12)}…
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={r.status}
                                                color={STATUS_COLOR[r.status]}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {r.error ?? describeCounts(r)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            {r.export_available && (
                                                <Button
                                                    size="small"
                                                    startIcon={<Download />}
                                                    onClick={() => {
                                                        void save(r)
                                                    }}
                                                >
                                                    NDJSON
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            </ReportStatus>

            <ConfirmDialog
                open={confirmErase}
                title="Erase this person's data?"
                message="Every event of this person is removed from the raw archive, exact events, queued events and the delivery log of this project. This cannot be undone."
                confirmLabel="Erase"
                confirmColor="error"
                onCancel={() => setConfirmErase(false)}
                onConfirm={() => {
                    setConfirmErase(false)
                    void submit('erasure')
                }}
            />
        </Box>
    )
}
